import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Upload, X, UserRound, ShieldCheck, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getUserSignature } from "lib/api/getUserSignature";
import { postUpdateUser } from "lib/api/postUpdateUser";
import { useAppStore } from "lib/store/useAppStore";
import {
  getUserSignatureProfile,
  SIGNATURE_IMAGE_MAX_SIZE,
  SIGNATURE_STAGE_SIZE,
} from "lib/utils/userSignatureProfile";
import "css/userSettings.css";

const EMAIL_DOMAIN_PRESETS = [
  "",
  "naver.com",
  "gmail.com",
  "daum.net",
  "hanmail.net",
  "nate.com",
];

export default function UserSettingsPage() {
  const { t } = useTranslation();
  const user = useAppStore((s) => s.user);
  const showAlert = useAppStore((s) => s.showAlert);
  const setAuthenticated = useAppStore((s) => s.setAuthenticated);
  const fileInputRef = useRef(null);
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const [local, setLocal] = useState("");
  const [domain, setDomain] = useState("");
  const [emailPreset, setEmailPreset] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [isPasswordsShow, setIsPasswordsShow] = useState(Array(3).fill(false));
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState("");
  const [existingSignatureUrl, setExistingSignatureUrl] = useState("");
  const [removeSignatureImage, setRemoveSignatureImage] = useState(false);

  const currentSignatureProfile = getUserSignatureProfile(user);
  const passwordMismatch = Boolean(confirmPassword && newPassword !== confirmPassword);

  useEffect(() => {
    if (!user) return;
    setLocal(user.local || "");
    setDomain(user.domain || "");
    setName(user.user_name || "");
    setExistingSignatureUrl(currentSignatureProfile?.imageUrl || "");
    setSignatureFile(null);
    setRemoveSignatureImage(false);
  }, [currentSignatureProfile?.imageUrl, user]);

  useEffect(() => {
    if (!user?.id) {
      setExistingSignatureUrl("");
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const result = await getUserSignature();
      if (cancelled) return;

      if (result?.success) {
        setExistingSignatureUrl(result.signatureImageUrl || "");
        return;
      }

      setExistingSignatureUrl(currentSignatureProfile?.imageUrl || "");
      console.warn("[UserSettingsPage] failed to load user signature", result);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentSignatureProfile?.imageUrl, user?.id]);

  useEffect(() => {
    if (!emailPreset) return;
    setDomain(emailPreset);
  }, [emailPreset]);

  useEffect(() => {
    if (signatureFile) {
      const objectUrl = URL.createObjectURL(signatureFile);
      setSignaturePreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    if (!removeSignatureImage && existingSignatureUrl) {
      setSignaturePreviewUrl(existingSignatureUrl);
      return undefined;
    }

    setSignaturePreviewUrl("");
    return undefined;
  }, [existingSignatureUrl, removeSignatureImage, signatureFile]);

  const handleDomainChange = (event) => {
    if (emailPreset) {
      setEmailPreset("");
    }
    setDomain(event.target.value);
  };

  const togglePasswordVisibility = (index) => {
    setIsPasswordsShow((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSelectSignatureClick = () => {
    fileInputRef.current?.click();
  };

  const handleSignatureFileChange = (event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) return;

    if (!nextFile.type.startsWith("image/")) {
      showAlert({
        text:
          t("userSetting.signature.invalidFileType", {
            defaultValue: "Only image files can be selected.",
          }) || "Only image files can be selected.",
        success: false,
        autoClose: true,
      });
      event.target.value = "";
      return;
    }

    setSignatureFile(nextFile);
    setRemoveSignatureImage(false);
  };

  const handleRemoveSignature = () => {
    setSignatureFile(null);
    setRemoveSignatureImage(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  async function handleSubmit(event) {
    event.preventDefault();
    if (!user?.id || savingRef.current) return;

    const localClean = String(local ?? "").trim();
    const domainClean = String(domain ?? "").replace(/\s+/g, "");
    const nameClean = String(name ?? "").trim();
    const newPw = String(newPassword ?? "");
    const confirmPw = String(confirmPassword ?? "");
    const email = `${localClean}@${domainClean}`;

    if (!localClean || !domainClean || !currentPassword) {
      return showAlert({
        textKey: "userSetting.alerts.requiredEmailCurrentPassword",
        success: false,
        autoClose: true,
      });
    }

    if ((newPw || confirmPw) && newPw !== confirmPw) {
      return showAlert({
        textKey: "userSetting.alerts.passwordMismatch",
        success: false,
        autoClose: true,
      });
    }

    const hasBasicInfoChanges =
      user.email !== email || user.user_name !== nameClean || Boolean(newPw);
    const hasSignatureImageChanges =
      Boolean(signatureFile) || removeSignatureImage;

    if (!hasBasicInfoChanges && !hasSignatureImageChanges) {
      return showAlert({
        textKey: "userSetting.alerts.noChanges",
        success: false,
        autoClose: true,
      });
    }

    const payload = {
      id: user.id,
      email,
      password: currentPassword,
      newPassword: newPw,
      name: nameClean,
      signatureImage: signatureFile,
      removeSignatureImage,
    };

    savingRef.current = true;
    setIsSaving(true);
    try {
      const {
        success,
        message,
        messageKey,
        messageParams,
        user: updatedUser,
      } = await postUpdateUser(payload);

      showAlert({
        text: message,
        messageKey,
        messageParams,
        success,
        autoClose: true,
      });

      if (success && updatedUser) {
        setAuthenticated(updatedUser);
        setSignatureFile(null);
        setRemoveSignatureImage(false);
        setExistingSignatureUrl(
          getUserSignatureProfile(updatedUser)?.imageUrl || "",
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      if (success) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch {
      showAlert({ text: t("userSetting.saveFailed", { defaultValue: "저장하지 못했습니다. 잠시 후 다시 시도해 주세요." }), success: false, autoClose: true });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  return (
    <section className="content userSetting userSettingsPage">
      <div className="userSettingsPage__heading">
        <h2><UserRound size={22} aria-hidden="true" />{t("userSetting.title")}</h2>
        <p>{t("userSetting.pageGuide", { defaultValue: "계정 정보와 보안을 관리하고, 보고서에 사용할 서명을 설정하세요." })}</p>
      </div>
      <div className="userSettingShell">
        <form onSubmit={handleSubmit} aria-busy={isSaving}>
        <fieldset className="userSettingForm userSettingsPage__fieldset" disabled={isSaving}>
          <legend className="userSettingsPage__srOnly">{t("userSetting.title")}</legend>
          <div className="userSettingMain">
            <div className="userSettingGroup">
              <div className="userSettingsPage__sectionHeading"><UserRound size={18} aria-hidden="true" /><div>
                <h3>{t("userSetting.basicInfo", { defaultValue: "기본 정보" })}</h3>
                <p>{t("userSetting.basicGuide", { defaultValue: "로그인 이메일과 사용자 이름을 확인하세요." })}</p>
              </div></div>
              <div className="userSettingFields">
                <div className="userSettingFormRow">
                  <label htmlFor="email-local">
                    {t("userSetting.labels.email")}
                  </label>
                  <div className="userSettingEmailControl">
                    <input
                      id="email-local"
                      type="text"
                      autoComplete="username"
                      required
                      value={local}
                      onChange={(event) => setLocal(event.target.value)}
                    />
                    <span>@</span>
                    <input
                      type="text"
                      aria-label={t("userSetting.email.domainLabel", { defaultValue: "이메일 도메인" })}
                      autoCapitalize="none"
                      required
                      value={domain}
                      onChange={handleDomainChange}
                    />
                    <select
                      onChange={(event) => setEmailPreset(event.target.value)}
                      value={emailPreset}
                      name="email-preset"
                      id="email-preset"
                      aria-label={t("userSetting.email.presetLabel", { defaultValue: "이메일 도메인 선택" })}
                    >
                      {EMAIL_DOMAIN_PRESETS.map((value) => (
                        <option key={value || "custom"} value={value}>
                          {value || t("userSetting.email.domainCustom")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="userSettingFormRow">
                  <label htmlFor="user-name">
                    {t("userSetting.labels.name")}
                  </label>
                  <div className="userSettingInputControl">
                    <input
                      id="user-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="userSettingGroup">
              <div className="userSettingsPage__sectionHeading"><ShieldCheck size={18} aria-hidden="true" /><div>
                <h3>{t("userSetting.security", { defaultValue: "비밀번호 및 보안" })}</h3>
                <p id="password-guide">{t("userSetting.securityGuide", { defaultValue: "저장하려면 현재 비밀번호가 필요합니다. 변경하지 않을 경우 새 비밀번호는 비워두세요." })}</p>
              </div></div>
              <div className="userSettingFields">
                <div className="userSettingFormRow">
                  <label htmlFor="password-current">
                    <mark>*</mark>
                    {t("userSetting.labels.currentPasswordRequired")}
                  </label>
                  <div className="userSettingPasswordControl">
                    <input
                      id="password-current"
                      autoComplete="current-password"
                      aria-describedby="password-guide"
                      required
                      type={isPasswordsShow[0] ? "text" : "password"}
                      value={currentPassword}
                      onChange={(event) => setCurrentPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(0)}
                      aria-label={
                        isPasswordsShow[0]
                          ? t("userSetting.buttons.hidePassword")
                          : t("userSetting.buttons.showPassword")
                      }
                    >
                      {isPasswordsShow[0] ? (
                        <Eye size={16} aria-hidden="true" />
                      ) : (
                        <EyeOff size={16} aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="userSettingFormRow">
                  <label htmlFor="password-new">
                    {t("userSetting.labels.newPassword")}
                  </label>
                  <div className="userSettingPasswordControl">
                    <input
                      id="password-new"
                      autoComplete="new-password"
                      type={isPasswordsShow[1] ? "text" : "password"}
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(1)}
                      aria-label={
                        isPasswordsShow[1]
                          ? t("userSetting.buttons.hidePassword")
                          : t("userSetting.buttons.showPassword")
                      }
                    >
                      {isPasswordsShow[1] ? (
                        <Eye size={16} aria-hidden="true" />
                      ) : (
                        <EyeOff size={16} aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="userSettingFormRow">
                  <label htmlFor="password-new-confirm">
                    {t("userSetting.labels.confirmPassword")}
                  </label>
                  <div className="userSettingPasswordControl">
                    <input
                      id="password-new-confirm"
                      autoComplete="new-password"
                      aria-invalid={passwordMismatch}
                      aria-describedby={passwordMismatch ? "password-mismatch" : undefined}
                      type={isPasswordsShow[2] ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(2)}
                      aria-label={
                        isPasswordsShow[2]
                          ? t("userSetting.buttons.hidePassword")
                          : t("userSetting.buttons.showPassword")
                      }
                    >
                      {isPasswordsShow[2] ? (
                        <Eye size={16} aria-hidden="true" />
                      ) : (
                        <EyeOff size={16} aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {passwordMismatch && <p id="password-mismatch" className="userSettingsPage__error" role="status">{t("userSetting.alerts.passwordMismatch")}</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="signatureSettingRow">
            <div className="userSettingsPage__sectionHeading"><PenLine size={18} aria-hidden="true" /><div><h3>
              {t("userSetting.signature.label", {
                defaultValue: "Signature image",
              })}
            </h3><p>{t("userSetting.signature.saveGuide", { defaultValue: "선택하거나 삭제한 서명은 아래 저장 버튼을 눌러야 적용됩니다." })}</p></div></div>
            <div className="signatureSettingPanel">
              <input
                ref={fileInputRef}
                id="signature-image-input"
                type="file"
                accept="image/*"
                onChange={handleSignatureFileChange}
                hidden
                aria-label={t("userSetting.signature.label", { defaultValue: "서명 이미지" })}
              />

              <div className="signatureSettingToolbar">
                <button
                  type="button"
                  className="signatureActionButton"
                  onClick={handleSelectSignatureClick}
                >
                  <Upload size={16} aria-hidden="true" />
                  <span>
                    {t("userSetting.signature.selectButton", {
                      defaultValue: "Upload image",
                    })}
                  </span>
                </button>

                <button
                  type="button"
                  className="signatureActionButton isDanger"
                  onClick={handleRemoveSignature}
                  disabled={!signaturePreviewUrl}
                >
                  <X size={16} aria-hidden="true" />
                  <span>
                    {t("userSetting.signature.removeButton", {
                      defaultValue: "Remove image",
                    })}
                  </span>
                </button>
              </div>

              <p className="signatureSettingHint">
                {t("userSetting.signature.hint", {
                  defaultValue:
                    "The report inserts the signature at a fixed bottom position. The original image size is preserved as much as possible within the maximum width and height.",
                })}
              </p>

              <div className="signaturePreviewCard">
                <div className="signaturePreviewHeader">
                  <span>
                    {t("userSetting.signature.previewTitle", {
                      defaultValue: "Signature preview",
                    })}
                  </span>
                  <strong>
                    {t("userSetting.signature.sizeGuide", {
                      defaultValue:
                        "{{stageWidth}} x {{stageHeight}} / max {{maxWidth}} x {{maxHeight}}",
                      stageWidth: SIGNATURE_STAGE_SIZE.width,
                      stageHeight: SIGNATURE_STAGE_SIZE.height,
                      maxWidth: SIGNATURE_IMAGE_MAX_SIZE.maxWidth,
                      maxHeight: SIGNATURE_IMAGE_MAX_SIZE.maxHeight,
                    })}
                  </strong>
                </div>

                <div
                  className="signaturePreviewStage"
                  style={{
                    width: `${SIGNATURE_STAGE_SIZE.width}px`,
                    height: `${SIGNATURE_STAGE_SIZE.height}px`,
                  }}
                >
                  <span className="signaturePreviewPlaceholder">
                    {t("userSetting.signature.placeholder", {
                      defaultValue: "(signature)",
                    })}
                  </span>

                  {signaturePreviewUrl ? (
                    <img
                      src={signaturePreviewUrl}
                      alt={t("userSetting.signature.previewAlt", {
                        defaultValue: "Signature preview",
                      })}
                      className="signaturePreviewImage"
                      style={{
                        maxWidth: `${SIGNATURE_IMAGE_MAX_SIZE.maxWidth}px`,
                        maxHeight: `${SIGNATURE_IMAGE_MAX_SIZE.maxHeight}px`,
                      }}
                    />
                  ) : (
                    <div className="signaturePreviewEmpty">
                      {t("userSetting.signature.empty", {
                        defaultValue: "No signature image selected.",
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="userSettingActions">
            <p role="status">{isSaving ? t("userSetting.saving", { defaultValue: "변경사항을 저장하고 있습니다…" }) : t("userSetting.saveGuide", { defaultValue: "정보를 확인한 후 변경사항을 저장하세요." })}</p>
            <button type="submit" disabled={isSaving}>{isSaving ? t("userSetting.savingButton", { defaultValue: "저장 중…" }) : t("userSetting.buttons.save")}</button>
          </div>
        </fieldset>
        </form>
      </div>
    </section>
  );
}
